import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import styled from "styled-components/macro";

import { reportsKeys } from "Reports/SavedReports/API/keys";

import FnrButton, { ButtonIcon, ButtonText } from "UI/FnrButton3";
import { FnrPopover, useKeepOpenList } from "UI/FnrPopover2";

import API from "../API";

import BoardButton from "./BoardButton";
import ButtonTooltip from "./ButtonTooltip";

export default function DeleteButton({ boardID, groupURL, isShared }) {
  const buttonRef = useRef();
  const [showTooltip, setShowTooltip] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletingBoard, setDeletingBoard] = useState(false);
  const queryClient = useQueryClient();

  const { boardID: boardIDParam } = useParams();
  const isReport = Boolean(boardIDParam);
  const location = useLocation();
  const history = useHistory();

  const { addReason, removeReason } = useKeepOpenList();

  useEffect(() => {
    if (showDeleteConfirmation) setShowTooltip(false);
  }, [showDeleteConfirmation]);

  const onShouldCloseDeleteMenu = () => {
    setShowDeleteConfirmation(false);
    removeReason("delete_board");
  };

  const toggleDeleteConfirmation = () => {
    setShowDeleteConfirmation((currentVal) => {
      if (currentVal) {
        removeReason("delete_board");
        return false;
      }

      addReason("delete_board");
      return true;
    });
  };

  const handleShowTooltip = () => {
    setShowTooltip(!showDeleteConfirmation);
  };

  const deleteBoard = () => {
    if (deletingBoard) return;

    setDeletingBoard(true);
    addReason("deleting_board");

    const deleteCall = API.deleteBoard({ boardID, groupURL });

    deleteCall
      .post()
      .then((res) => {
        //need to update tabs and go to board... but for now, we redirect
        if (isReport) {
          setDeletingBoard(false);
          queryClient.invalidateQueries(reportsKeys.getReportsList());
          history.push("/reports");
        } else {
          window.location = "/" + groupURL;
        }
      })
      .catch((err) => {
        console.log(err);
        setDeletingBoard(false);
      });
  };

  return (
    <>
      <DeleteButtonDiv
        className={
          "fas fa-trash-alt " + (showDeleteConfirmation ? "focused" : "")
        }
        ref={buttonRef}
        data-cy="deleteBoardSheetButton"
        onClick={toggleDeleteConfirmation}
        onMouseEnter={handleShowTooltip}
        onMouseLeave={() => setShowTooltip(false)}
      />
      <ButtonTooltip
        show={showTooltip}
        buttonRef={buttonRef}
        content="Delete Report"
      />
      <FnrPopover
        style={{
          backgroundImage:
            "linear-gradient( to right bottom, rgba(180, 37, 45, 0.7), rgba(128, 36, 40, 0.7) )",
        }}
        show={showDeleteConfirmation}
        buttonRef={buttonRef}
        onShouldClose={onShouldCloseDeleteMenu}
      >
        <Confirmation>
          Are you sure you want to delete this report?
          <DeleteSubtext>
            {isShared
              ? "This report will be removed from this group, but may still remain in other groups that it is shared with."
              : "This cannot be undone and all data will be lost."}
          </DeleteSubtext>
          <ButtonRow>
            <FnrButton
              data-cy="cancelDeleteBoardButton"
              onClick={toggleDeleteConfirmation}
              style={{ marginRight: 10 }}
              disabled={deletingBoard}
            >
              Cancel
            </FnrButton>
            <FnrButton
              color="red"
              data-cy="nukeItBoardButton"
              onClick={deleteBoard}
              loading={deletingBoard}
            >
              <ButtonIcon fas fa-trash />
              <ButtonText>Delete</ButtonText>
            </FnrButton>
          </ButtonRow>
        </Confirmation>
      </FnrPopover>
    </>
  );
}

const Confirmation = styled.div`
  font-size: 20px;
  padding: 8px;
  max-width: 260px;
  box-sizing: border-box;
`;

const DeleteSubtext = styled.div`
  font-size: 14px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
`;

const DeleteButtonDiv = styled(BoardButton)`
  &:hover {
    background: #f42434;
  }
  &:active,
  &.focused {
    background: #b4252d;
    color: #fff;
  }
`;
